import React, { useState, useRef } from 'react'
import { TableDensity } from '@/shared/hooks/useTableCustomization'
import { useTranslation } from '@/shared/contexts/TranslationContext'

// Ícones SVG simples
const Icons = {
  Settings: ({ className }: { className?: string }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  ),
  Eye: ({ className }: { className?: string }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  ),
  EyeOff: ({ className }: { className?: string }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  ),
  RotateCcw: ({ className }: { className?: string }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="1 4 1 10 7 10" />
      <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
    </svg>
  ),
  GripVertical: ({ className }: { className?: string }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="9" cy="5" r="1" />
      <circle cx="9" cy="12" r="1" />
      <circle cx="9" cy="19" r="1" />
      <circle cx="15" cy="5" r="1" />
      <circle cx="15" cy="12" r="1" />
      <circle cx="15" cy="19" r="1" />
    </svg>
  ),
  Check: ({ className }: { className?: string }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  )
}

interface TableCustomizationMenuProps {
  columns: Array<{
    key: string
    label: string
    visible: boolean
  }>
  density: TableDensity
  onToggleColumn: (key: string) => void
  onReorderColumns: (fromIndex: number, toIndex: number) => void
  onChangeDensity: (density: TableDensity) => void
  onReset: () => void
}

export function TableCustomizationMenu({
  columns,
  density,
  onToggleColumn,
  onReorderColumns,
  onChangeDensity,
  onReset
}: TableCustomizationMenuProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null)
  const dragOverIndex = useRef<number | null>(null)

  const { t } = useTranslation()

  const handleDragStart = (index: number) => {
    setDraggedIndex(index)
  }

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault()
    dragOverIndex.current = index
  }

  const handleDragEnd = () => {
    if (draggedIndex !== null && dragOverIndex.current !== null && draggedIndex !== dragOverIndex.current) {
      onReorderColumns(draggedIndex, dragOverIndex.current)
    }
    setDraggedIndex(null)
    dragOverIndex.current = null
  }

  const densityOptions: Array<{ value: TableDensity; label: string; description: string }> = [
    { value: 'compact', label: t('customization.table.density.compact.label'), description: t('customization.table.density.compact.description') },
    { value: 'normal', label: t('customization.table.density.normal.label'), description: t('customization.table.density.normal.description') },
    { value: 'comfortable', label: t('customization.table.density.comfortable.label'), description: t('customization.table.density.comfortable.description') }
  ]

  return (
    <div className="relative">
      {/* Botão de abrir menu */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="p-2.5 rounded-lg transition-colors bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700 border border-slate-200 dark:border-slate-700"
        title={t('customization.table.title')}
      >
        <Icons.Settings className="w-5 h-5" />
      </button>

      {/* Menu dropdown */}
      {isOpen && (
        <>
          {/* Overlay para fechar ao clicar fora */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />

          {/* Conteúdo do menu */}
          <div className="absolute right-0 mt-2 w-80 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg z-50 overflow-hidden">
            {/* Cabeçalho */}
            <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                {t('customization.table.title')}
              </h3>
              <button
                onClick={onReset}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-slate-600 dark:text-slate-400 hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors"
                title={t('customization.table.restore')}
              >
                <Icons.RotateCcw className="w-3 h-3" />
                <span>{t('customization.table.restore')}</span>
              </button>
            </div>

            <div className="max-h-96 overflow-y-auto">
              {/* Seção de Densidade */}
              <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700">
                <h4 className="text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-2">
                  {t('customization.table.density.title')}
                </h4>
                <div className="space-y-1">
                  {densityOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => onChangeDensity(option.value)}
                      className={`w-full flex items-center justify-between px-3 py-2 rounded-md transition-colors ${
                        density === option.value
                          ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400'
                          : 'hover:bg-slate-50 dark:hover:bg-slate-700/50 text-slate-700 dark:text-slate-300'
                      }`}
                    >
                      <div className="text-left">
                        <div className="text-sm font-medium">{option.label}</div>
                        <div className="text-xs text-slate-500 dark:text-slate-400">
                          {option.description}
                        </div>
                      </div>
                      {density === option.value && (
                        <Icons.Check className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Seção de Colunas */}
              <div className="px-4 py-3">
                <div className="flex items-start justify-between mb-2">
                  <h4 className="text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                    Colunas
                  </h4>
                  <div className="text-xs text-slate-500 dark:text-slate-400 italic max-w-[180px] text-right leading-tight">
                    {t('customization.table.columns.hint')}
                  </div>
                </div>
                <div className="space-y-1">
                  {columns.map((column, index) => (
                    <div
                      key={column.key}
                      draggable
                      onDragStart={() => handleDragStart(index)}
                      onDragOver={(e) => handleDragOver(e, index)}
                      onDragEnd={handleDragEnd}
                      className={`flex items-center gap-2 px-2 py-2 rounded-md cursor-move transition-colors ${
                        draggedIndex === index
                          ? 'bg-emerald-100 dark:bg-emerald-900/30 opacity-50'
                          : 'hover:bg-slate-50 dark:hover:bg-slate-700/50'
                      }`}
                    >
                      {/* Ícone de drag */}
                      <Icons.GripVertical className="w-4 h-4 text-slate-400 dark:text-slate-500 flex-shrink-0" />

                      {/* Nome da coluna */}
                      <span className="flex-1 text-sm text-slate-700 dark:text-slate-300">
                        {column.label}
                      </span>

                      {/* Botão de visibilidade */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          onToggleColumn(column.key)
                        }}
                        className={`flex-shrink-0 p-1 rounded transition-colors ${
                          column.visible
                            ? 'text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/20'
                            : 'text-slate-400 dark:text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700'
                        }`}
                        title={column.visible ? t('customization.table.columns.hide') : t('customization.table.columns.show')}
                      >
                        {column.visible ? (
                          <Icons.Eye className="w-4 h-4" />
                        ) : (
                          <Icons.EyeOff className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
